import { layerNaturalSize } from './model/bounds';
import { findLayer, isGroup, leafContexts } from './model/tree';
import type { EditorDocument, Layer, Rect, Vec2 } from './model/types';
import {
  HANDLE_CURSORS,
  type HandleId,
  hitTestHandle,
  isInsideRect,
  rectEdges,
  resizeRect,
  type SnapTargets,
  snapRect,
} from './selection';

/**
 * Move-tool maths: which layer is under a point, and what a drag does to it.
 *
 * Everything here answers from the **model**, never from the renderer: the
 * stage's pointer hooks already own input for painting, shapes and text, and
 * the document is the only source of truth. A layer's footprint is derived
 * from its own fields, so hit-testing cannot drift from what the engine draws.
 *
 * Rotation is handled by inverse-rotating the point about the layer's centre
 * into the layer's unrotated space, so the rectangle maths stays the boring
 * kind. Rotation is always about the layer centre — the same origin the
 * engine and the export use.
 */

/** The transform fields a move or transform session can change. */
export interface LayerTransform {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  angle: number;
}

/** A layer a move tool can select, hit-test and transform. */
export function isMovable(layer: Layer): boolean {
  // A fill layer is drawn as the whole document whatever its x/y says, so
  // moving it would do nothing visible — refusing beats pretending.
  // Adjustment layers have no pixels to grab yet (no door until Iteration 11).
  // A group is a folder: it has no position of its own, so its children are
  // what gets moved.
  return layer.type !== 'fill' && layer.type !== 'adjustment' && layer.type !== 'group';
}

/**
 * The layer's footprint in document space, including scale but not rotation.
 *
 * Size and scale come from `core/model/bounds` — the one place that knows how
 * big every layer type is. Fill and adjustment layers are refused by
 * `isMovable` before this is called, and shape layers have no footprint yet,
 * so the document size is never needed here.
 */
export function layerBounds(layer: Layer): Rect | null {
  if (!isMovable(layer)) return null;
  const natural = layerNaturalSize(layer);
  if (!natural) return null;
  return {
    x: layer.x,
    y: layer.y,
    width: natural.width * layer.scaleX,
    height: natural.height * layer.scaleY,
  };
}

/** The centre of the layer's bounds — the origin every rotation uses. */
export function boundsCenter(bounds: Rect): Vec2 {
  return { x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height / 2 };
}

/** Undo a rotation of `degrees` about `centre` for `point`. */
function inverseRotate(point: Vec2, centre: Vec2, degrees: number): Vec2 {
  if (degrees === 0) return point;
  const radians = (-degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - centre.x;
  const dy = point.y - centre.y;
  return {
    x: centre.x + dx * cos - dy * sin,
    y: centre.y + dx * sin + dy * cos,
  };
}

/** Does `point` land on this layer's (possibly rotated) footprint? */
export function hitTestLayer(layer: Layer, point: Vec2): boolean {
  const bounds = layerBounds(layer);
  if (!bounds) return false;
  const local = inverseRotate(point, boundsCenter(bounds), layer.angle);
  return isInsideRect(bounds, local);
}

/**
 * The topmost visible, unlocked, movable layer under `point`.
 *
 * Hidden and locked layers are skipped rather than refused: clicking through
 * them is what every editor does, and the Layers panel is where you deal with
 * a locked layer.
 *
 * The stack is a tree, so the leaves are walked in z-order — a layer inside a
 * group is grabbed exactly like a layer at the top, and a group that is hidden
 * or locked hides or locks its children with it.
 */
export function hitTestStack(doc: EditorDocument, point: Vec2): Layer | null {
  const contexts = leafContexts(doc.layers);
  for (let index = contexts.length - 1; index >= 0; index -= 1) {
    const context = contexts[index];
    if (!context) continue;
    const { layer, visible, unlocked } = context;
    if (!visible || !unlocked) continue;
    if (!layer.visible || layer.locked || !isMovable(layer)) continue;
    if (hitTestLayer(layer, point)) return layer;
  }
  return null;
}

/**
 * Which transform handle is under `point`, or null.
 *
 * The transform overlay's handles live in the layer's rotated space — the
 * overlay box rotates with the layer — so the point is inverse-rotated about
 * the centre first and the plain marquee maths does the rest. The rotate knob
 * sits `rotateOffset` above the top-centre; callers scale both this and the
 * tolerance by 1/zoom so the grab area stays a constant size on screen.
 */
export type TransformHandleId = HandleId | 'rotate';

/** Cursor per transform handle, plus the body of the layer itself. */
export const TRANSFORM_CURSORS: Record<string, string> = {
  ...HANDLE_CURSORS,
  rotate: 'crosshair',
  move: 'move',
};

export function hitTestTransformHandle(
  bounds: Rect,
  angle: number,
  point: Vec2,
  options: { tolerance?: number; rotateOffset?: number } = {},
): TransformHandleId | null {
  const tolerance = options.tolerance ?? 8;
  const centre = boundsCenter(bounds);
  const local = inverseRotate(point, centre, angle);
  const knob = { x: centre.x, y: bounds.y - (options.rotateOffset ?? 24) };
  if (Math.abs(local.x - knob.x) <= tolerance && Math.abs(local.y - knob.y) <= tolerance) {
    return 'rotate';
  }
  return hitTestHandle(bounds, local, tolerance);
}

export interface MoveResult {
  x: number;
  y: number;
  snappedX: number | null;
  snappedY: number | null;
}

/**
 * Where a dragged layer lands: its start bounds plus the pointer delta, with
 * edges and centre snapped to the given targets. Snapping is measured in
 * document pixels, so the caller scales the threshold by 1/zoom.
 */
export function moveForDrag(
  bounds: Rect,
  delta: Vec2,
  targets: SnapTargets,
  threshold = 6,
): MoveResult {
  const moved = { ...bounds, x: bounds.x + delta.x, y: bounds.y + delta.y };
  const snapped = snapRect(moved, targets, threshold);
  // Unlike the crop marquee, a layer may hang off the canvas — so no clamping.
  return {
    x: snapped.rect.x,
    y: snapped.rect.y,
    snappedX: snapped.snappedX,
    snappedY: snapped.snappedY,
  };
}

/** Nudge the layer's position; the caller rounds to whole pixels. */
export function nudge(layer: Layer, dx: number, dy: number): LayerTransform {
  return {
    x: layer.x + dx,
    y: layer.y + dy,
    scaleX: layer.scaleX,
    scaleY: layer.scaleY,
    angle: layer.angle,
  };
}

export interface ScaleResult {
  scaleX: number;
  scaleY: number;
  x: number;
  y: number;
}

/** A bounds so large nothing clamps; layers are allowed off-canvas. */
const UNBOUNDED: Rect = { x: -1e9, y: -1e9, width: 2e9, height: 2e9 };

/** Apply a rotation of `degrees` about `centre` to `point`. */
function rotatePoint(point: Vec2, centre: Vec2, degrees: number): Vec2 {
  if (degrees === 0) return point;
  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const dx = point.x - centre.x;
  const dy = point.y - centre.y;
  return {
    x: centre.x + dx * cos - dy * sin,
    y: centre.y + dx * sin + dy * cos,
  };
}

/**
 * The point opposite the dragged handle — the one that must not move.
 * Corner handles anchor the opposite corner; edge handles anchor the
 * midpoint of the opposite edge.
 */
function oppositeAnchor(bounds: Rect, handle: HandleId): Vec2 {
  const { left, top, right, bottom } = rectEdges(bounds);
  const cx = (left + right) / 2;
  const cy = (top + bottom) / 2;
  if (handle === 'nw') return { x: right, y: bottom };
  if (handle === 'ne') return { x: left, y: bottom };
  if (handle === 'se') return { x: left, y: top };
  if (handle === 'sw') return { x: right, y: top };
  if (handle === 'n') return { x: cx, y: bottom };
  if (handle === 's') return { x: cx, y: top };
  if (handle === 'w') return { x: right, y: cy };
  return { x: left, y: cy };
}

/**
 * Scale by dragging a handle on the layer's bounds.
 *
 * The handle maths is the crop marquee's (`resizeRect`), but a layer is not a
 * marquee: the result is converted back into a scale factor plus a position,
 * and the point **opposite the dragged handle stays exactly where it was** —
 * grab the south-east corner and the north-west corner does not move, which is
 * what every direct-manipulation editor does. With the layer rotated the
 * anchor is computed in rotated space, because rotation is about the centre:
 * a naive unrotated correction would slide the anchor sideways the moment the
 * layer is not axis-aligned.
 */
export function scaleForDrag(
  layer: Layer,
  bounds: Rect,
  handle: HandleId,
  delta: Vec2,
  options: { aspectLock: boolean; minimum?: number } = { aspectLock: false },
): ScaleResult {
  // The natural size is derived from the bounds divided by the current scale,
  // which works for every movable layer type — image, text and paint alike —
  // without this module needing to know how each type measures itself again.
  const naturalWidth = bounds.width / (layer.scaleX || 1);
  const naturalHeight = bounds.height / (layer.scaleY || 1);

  const aspect = options.aspectLock ? bounds.width / bounds.height : undefined;
  // The pointer delta arrives in document space; the bounds live in the
  // layer's unrotated space. Pull the delta back into that space so the
  // handle follows the finger even when the layer is rotated.
  const radians = (-layer.angle * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  const localDelta = {
    x: delta.x * cos - delta.y * sin,
    y: delta.x * sin + delta.y * cos,
  };
  const resized = resizeRect(bounds, handle, localDelta, UNBOUNDED, {
    aspect,
    minimum: options.minimum ?? 1,
  });
  if (resized.width === bounds.width && resized.height === bounds.height) {
    return { scaleX: layer.scaleX, scaleY: layer.scaleY, x: layer.x, y: layer.y };
  }

  // Where the anchor sits on screen now, and where it would sit if the new
  // bounds simply replaced the old ones. The difference is how far the new
  // centre must shift to pull the anchor back.
  const anchor = rotatePoint(oppositeAnchor(bounds, handle), boundsCenter(bounds), layer.angle);
  const after = boundsCenter(resized);
  const anchorAfter = rotatePoint(oppositeAnchor(resized, handle), after, layer.angle);
  return {
    scaleX: resized.width / naturalWidth,
    scaleY: resized.height / naturalHeight,
    x: layer.x + (anchor.x - anchorAfter.x),
    y: layer.y + (anchor.y - anchorAfter.y),
  };
}

export interface RotateResult {
  angle: number;
  snapped: boolean;
}

/**
 * Rotate by dragging: the angle from the layer's centre to the pointer,
 * measured from straight up (so grabbing the handle and pulling right turns
 * clockwise). With `snap`, the angle is pulled to the nearest 15° within
 * `snapThreshold` degrees.
 */
export function rotateForDrag(
  centre: Vec2,
  pointer: Vec2,
  startAngle: number,
  grabAngle: number,
  options: { snap: boolean; snapThreshold?: number } = { snap: false },
): RotateResult {
  let angle = normaliseDegrees(startAngle + (angleFromCentre(centre, pointer) - grabAngle));
  let snapped = false;
  if (options.snap) {
    const step = 15;
    const nearest = Math.round(angle / step) * step;
    if (Math.abs(angle - nearest) <= (options.snapThreshold ?? 4)) {
      angle = normaliseDegrees(nearest);
      snapped = true;
    }
  }
  return { angle, snapped };
}

/* ---------------------------------------------------------------------- */
/* Joint transforms — the selection set (or a group) as one layer.         */
/*                                                                        */
/* The box the user drags is the union of the members' unrotated bounds.   */
/* It is never itself rotated — members keep their own angles — so every   */
/* anchor below is plain rectangle maths. A member's *centre* is the one   */
/* point that survives every mapping here, for the same reason             */
/* `planRotate90` moves layers by their centre: `x/y` is the unrotated     */
/* top-left, and the centre is where the rotation origin lives.            */
/* ---------------------------------------------------------------------- */

/** One layer a joint drag transforms: its id, start transform and start bounds. */
export interface SetMember {
  id: string;
  start: LayerTransform;
  /** The member's unrotated drawn bounds — position plus drawn size. */
  bounds: Rect;
}

export interface SetScaleResult {
  /** Fresh transforms, aligned with the input member order. */
  transforms: LayerTransform[];
  /** The union box after the scale — what the overlay draws next. */
  bounds: Rect;
}

export interface SetRotateResult {
  /** The shared delta every member composed, normalised to (-180, 180]. */
  delta: number;
  snapped: boolean;
  /** Fresh transforms, aligned with the input member order. */
  transforms: LayerTransform[];
}

/**
 * Scale a set by dragging a handle on its union box.
 *
 * The union resizes with the marquee maths (opposite edge pinned, aspect lock
 * honoured); each member then maps by its centre — the centre's offset from
 * the pinned anchor scales by the same factors, and the member's own scale
 * multiplies them too. Returns null when the drag moves nothing, or when the
 * drag would stretch non-uniformly over a rotated member — an edge handle by
 * definition, an unlocked corner when Shift is held — because that geometry
 * is a skew the model cannot express. The overlay does not offer those
 * handles in that case, and this contract makes the refusal real.
 */
export function scaleSetForDrag(
  members: SetMember[],
  union: Rect,
  handle: HandleId,
  delta: Vec2,
  options: { aspectLock: boolean; minimum?: number } = { aspectLock: false },
): SetScaleResult | null {
  // Any mapping with fx ≠ fy over a rotated member is a skew the model cannot
  // express — an edge handle is non-uniform by definition, and an unlocked
  // corner (Shift) can become one. Both refuse; the overlay does not offer
  // them in that case, and this contract makes the refusal real.
  const nonUniform = handle.length === 1 || !options.aspectLock;
  if (nonUniform && members.some((member) => member.start.angle % 360 !== 0)) return null;
  if (union.width <= 0 || union.height <= 0) return null;

  const aspect = options.aspectLock ? union.width / union.height : undefined;
  const resized = resizeRect(union, handle, delta, UNBOUNDED, {
    aspect,
    minimum: options.minimum ?? 1,
  });
  if (resized.width === union.width && resized.height === union.height) return null;

  const fx = resized.width / union.width;
  const fy = resized.height / union.height;
  // The union box is never rotated, so the anchor needs no rotation of its own.
  const anchor = oppositeAnchor(union, handle);

  const transforms = members.map((member): LayerTransform => {
    const centre = boundsCenter(member.bounds);
    const mapped = {
      x: anchor.x + (centre.x - anchor.x) * fx,
      y: anchor.y + (centre.y - anchor.y) * fy,
    };
    return {
      x: mapped.x - (member.bounds.width * fx) / 2,
      y: mapped.y - (member.bounds.height * fy) / 2,
      scaleX: member.start.scaleX * fx,
      scaleY: member.start.scaleY * fy,
      angle: member.start.angle,
    };
  });
  return { transforms, bounds: resized };
}

/**
 * Rotate a set by dragging the knob above its union box.
 *
 * One shared delta — measured from straight up like the single-layer knob,
 * snapped to 15° under Shift — composes onto every member's angle, and every
 * member's centre orbits the union centre by it. A member's own rotation
 * never changes what the orbit does, which is why the joint rotate is simpler
 * than the joint scale.
 */
export function rotateSetForDrag(
  members: SetMember[],
  centre: Vec2,
  pointer: Vec2,
  grabAngle: number,
  options: { snap: boolean; snapThreshold?: number } = { snap: false },
): SetRotateResult {
  let delta = normaliseDegrees(angleFromCentre(centre, pointer) - grabAngle);
  let snapped = false;
  if (options.snap) {
    const step = 15;
    const nearest = Math.round(delta / step) * step;
    if (Math.abs(delta - nearest) <= (options.snapThreshold ?? 4)) {
      delta = normaliseDegrees(nearest);
      snapped = true;
    }
  }
  const transforms = members.map((member): LayerTransform => {
    const moved = rotatePoint(boundsCenter(member.bounds), centre, delta);
    return {
      x: moved.x - member.bounds.width / 2,
      y: moved.y - member.bounds.height / 2,
      scaleX: member.start.scaleX,
      scaleY: member.start.scaleY,
      angle: normaliseDegrees(member.start.angle + delta),
    };
  });
  return { delta, snapped, transforms };
}

/* ---------------------------------------------------------------------- */
/* The members of a joint drag.                                            */
/*                                                                        */
/* A set is its movable leaves; a selected group is its leaf descendants.  */
/* Both go through the same filter the move tool has always applied to a  */
/* click — visible, unlocked, movable — so a locked member is left behind */
/* by a drag rather than refused, exactly as Iteration 12 ruled.          */
/* ---------------------------------------------------------------------- */

/**
 * The layers a joint drag over `ids` transforms, in z-order.
 *
 * A selected group contributes its leaf descendants — so a set naming a
 * group and one of its children moves each leaf once, and a locked or
 * hidden group contributes nothing (its leaves neither render nor grab).
 */
export function jointMembers(doc: EditorDocument, ids: ReadonlySet<string>): SetMember[] {
  const wanted = new Set<string>();
  const collectLeaves = (siblings: Layer[]) => {
    for (const layer of siblings) {
      if (isGroup(layer)) collectLeaves(layer.children);
      else wanted.add(layer.id);
    }
  };
  const collect = (siblings: Layer[]) => {
    for (const layer of siblings) {
      if (ids.has(layer.id)) {
        if (isGroup(layer)) collectLeaves(layer.children);
        else wanted.add(layer.id);
        continue;
      }
      if (isGroup(layer)) collect(layer.children);
    }
  };
  collect(doc.layers);

  const out: SetMember[] = [];
  for (const context of leafContexts(doc.layers)) {
    const { layer, visible, unlocked } = context;
    if (!wanted.has(layer.id)) continue;
    if (!visible || !unlocked || !layer.visible || layer.locked || !isMovable(layer)) continue;
    const bounds = layerBounds(layer);
    if (!bounds) continue;
    out.push({ id: layer.id, start: transformOf(layer), bounds });
  }
  return out;
}

/** The union of the members' unrotated bounds — the box the user drags. */
export function unionBounds(members: SetMember[]): Rect | null {
  let bounds: Rect | null = null;
  for (const member of members) {
    bounds = unionRect(bounds, member.bounds);
  }
  return bounds;
}

/** The union of two rects, treating null as "nothing yet". */
export function unionRect(a: Rect | null, b: Rect): Rect {
  if (!a) return { ...b };
  const right = Math.max(a.x + a.width, b.x + b.width);
  const bottom = Math.max(a.y + a.height, b.y + b.height);
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: right - Math.min(a.x, b.x),
    height: bottom - Math.min(a.y, b.y),
  };
}

/**
 * The group's drawn footprint: the union of its visible leaves' bounds,
 * locked children included — they render, they just do not grab. This is
 * what a click test runs against when the group is locked and the move tool
 * must say so out loud instead of silently doing nothing.
 */
export function groupFootprint(doc: EditorDocument, groupId: string): Rect | null {
  const group = findLayer(doc.layers, groupId);
  if (!group || !isGroup(group) || !group.visible) return null;
  let bounds: Rect | null = null;
  for (const context of leafContexts(group.children)) {
    const layer = context.layer;
    if (!context.visible || !layer.visible) continue;
    const one = layerBounds(layer);
    if (!one) continue;
    bounds = unionRect(bounds, one);
  }
  return bounds;
}

function transformOf(layer: Layer): LayerTransform {
  return { x: layer.x, y: layer.y, scaleX: layer.scaleX, scaleY: layer.scaleY, angle: layer.angle };
}

/** Fold any degree value into (-180, 180]. */
export function normaliseDegrees(degrees: number): number {
  let angle = degrees % 360;
  if (angle > 180) angle -= 360;
  if (angle <= -180) angle += 360;
  // Fold -0 into 0: the negated angle of an unrotated layer should not leak
  // a signed zero into the document.
  return angle === 0 ? 0 : angle;
}

/** The angle from `centre` to `pointer`, measured from straight up, clockwise. */
export function angleFromCentre(centre: Vec2, pointer: Vec2): number {
  return (Math.atan2(pointer.x - centre.x, -(pointer.y - centre.y)) * 180) / Math.PI;
}

/** True when a drag moved or scaled nothing enough to be worth a commit. */
export function isNegligibleTransform(before: LayerTransform, after: LayerTransform): boolean {
  const same = (a: number, b: number) => Math.abs(a - b) < 1e-6;
  return (
    same(before.x, after.x) &&
    same(before.y, after.y) &&
    same(before.scaleX, after.scaleX) &&
    same(before.scaleY, after.scaleY) &&
    same(before.angle, after.angle)
  );
}
